import React, { useState, type KeyboardEvent } from "react";
import { EyeIcon, EyeOffIcon, Spinner } from "./Shared/Icons";
import { LoadingButton } from "../../../components/LoadingButton";
import { ErrorAlert } from "../../../components/ErrorAlert";
import type { useLogin } from "../hooks/useLogin";
import googleSvg from "../../../assets/google.svg";

interface LoginViewProps {
  auth: ReturnType<typeof useLogin>;
  isDark: boolean;
  onShowRegister: () => void;
  onShowRecover: () => void;
}

export const LoginView: React.FC<LoginViewProps> = ({
  auth,
  isDark,
  onShowRegister,
  onShowRecover,
}) => {
  const {
    username,
    password,
    error,
    isLoading,
    setUsername,
    setPassword,
    setError,
    handleLogin,
    handleSocialLogin,
  } = auth;

  const [showPass, setShowPass] = useState(false);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isLoading) {
      handleLogin();
    }
  };

  const inputClass =
    "w-full py-4 px-5 border border-border bg-surface text-text placeholder-text-dim rounded-xl text-xs font-medium transition-all focus:border-accent focus:bg-background";

  return (
    <div className="flex flex-col gap-4">
      {error && <ErrorAlert error={error} onDismiss={() => setError("")} className="mt-2" />}

      {/* Email input */}
      <input
        type="text"
        placeholder="Usuario o Email"
        autoComplete="username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={isLoading}
        className={inputClass}
        autoFocus
      />

      {/* Password input */}
      <div className="relative">
        <input
          type={showPass ? "text" : "password"}
          placeholder="Contraseña"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={isLoading}
          className={inputClass}
        />
        <button
          type="button"
          onClick={() => setShowPass(!showPass)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent transition-all text-xs flex items-center justify-center"
        >
          {showPass ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>

      <button
        onClick={onShowRecover}
        className="text-xs font-bold text-left self-start mt-1 cursor-pointer transition-all text-text-muted hover:text-accent"
      >
        ¿Olvidaste tu contraseña?
      </button>

      <LoadingButton
        onClick={handleLogin}
        isLoading={isLoading}
        className="w-full py-4 bg-accent hover:bg-accent/80 disabled:opacity-60 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-accent/20 active:scale-[0.98] mt-4"
      >
        {isLoading ? <span>Ingresando...</span> : "Ingresar"}
      </LoadingButton>

      <div className="flex items-center my-1 text-xs text-gray-400">
        <div className="flex-grow border-t border-gray-300 dark:border-white/10"></div>
        <span className="px-3 text-[10px] font-bold uppercase tracking-wider text-gray-400/80">o continuar con</span>
        <div className="flex-grow border-t border-gray-300 dark:border-white/10"></div>
      </div>

      <button
        type="button"
        onClick={() => handleSocialLogin("Google")}
        disabled={isLoading}
        className="w-full py-4 rounded-xl text-sm font-semibold transition-all active:scale-[0.98] flex items-center justify-center gap-2.5 border bg-surface text-text border-border hover:bg-surface-hover shadow-sm"
      >
        <img src={googleSvg} alt="Google" className="w-5 h-5" />
        <span>Google</span>
      </button>

      <button
        type="button"
        onClick={onShowRegister}
        className="text-xs font-bold text-center mt-3 cursor-pointer transition-all text-text-muted hover:text-accent"
      >
        ¿No tenés cuenta? Registrate
      </button>
    </div>
  );
};
